/**
 * Dawn Performance Monitor
 * Tracks and reports performance metrics for optimization monitoring
 */

class PerformanceMonitor {
  constructor() {
    this.metrics = {};
    this.startTime = performance.now();
  }

  /**
   * Measure page load performance
   */
  measurePageLoad() {
    // Wait for page to fully load
    if (document.readyState === 'complete') {
      this.calculateMetrics();
    } else {
      window.addEventListener('load', () => {
        this.calculateMetrics();
      });
    }
  }

  /**
   * Calculate and log performance metrics
   */
  calculateMetrics() {
    const navigation = performance.getEntriesByType('navigation')[0];
    const resources = performance.getEntriesByType('resource');

    this.metrics = {
      // Page load times
      domContentLoaded: navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart,
      loadComplete: navigation.loadEventEnd - navigation.loadEventStart,
      totalLoadTime: navigation.loadEventEnd - navigation.fetchStart,

      // Resource counts
      totalResources: resources.length,
      jsResources: resources.filter((r) => r.name.includes('.js')).length,
      cssResources: resources.filter((r) => r.name.includes('.css')).length,

      // Resource sizes
      totalSize: resources.reduce((sum, r) => sum + (r.transferSize || 0), 0),
      jsSize: resources.filter((r) => r.name.includes('.js')).reduce((sum, r) => sum + (r.transferSize || 0), 0),
      cssSize: resources.filter((r) => r.name.includes('.css')).reduce((sum, r) => sum + (r.transferSize || 0), 0),

      // Core Web Vitals (if available)
      lcp: this.getLCP(),
      fid: this.getFID(),
      cls: this.getCLS(),
    };

    this.logMetrics();
  }

  /**
   * Get Largest Contentful Paint
   */
  getLCP() {
    return new Promise((resolve) => {
      if ('PerformanceObserver' in window) {
        const observer = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const lastEntry = entries[entries.length - 1];
          resolve(lastEntry.startTime);
        });
        observer.observe({ entryTypes: ['largest-contentful-paint'] });
      } else {
        resolve(null);
      }
    });
  }

  /**
   * Get First Input Delay
   */
  getFID() {
    return new Promise((resolve) => {
      if ('PerformanceObserver' in window) {
        const observer = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          resolve(entries[0].processingStart - entries[0].startTime);
        });
        observer.observe({ entryTypes: ['first-input'] });
      } else {
        resolve(null);
      }
    });
  }

  /**
   * Get Cumulative Layout Shift
   */
  getCLS() {
    return new Promise((resolve) => {
      if ('PerformanceObserver' in window) {
        let clsValue = 0;
        const observer = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (!entry.hadRecentInput) {
              clsValue += entry.value;
            }
          }
        });
        observer.observe({ entryTypes: ['layout-shift'] });

        // Resolve after a delay to capture CLS
        setTimeout(() => resolve(clsValue), 5000);
      } else {
        resolve(null);
      }
    });
  }

  /**
   * Check if a script is loaded
   */
  isScriptLoaded(scriptName) {
    return Array.from(document.scripts).some((script) => script.src && script.src.includes(scriptName));
  }

  /**
   * Log metrics to console
   */
  logMetrics() {
    console.group('🚀 Dawn Performance Metrics');
    console.log('📊 Page Load Performance:');
    console.log(`   DOM Content Loaded: ${this.metrics.domContentLoaded.toFixed(2)}ms`);
    console.log(`   Load Complete: ${this.metrics.loadComplete.toFixed(2)}ms`);
    console.log(`   Total Load Time: ${this.metrics.totalLoadTime.toFixed(2)}ms`);

    console.log('📦 Resource Summary:');
    console.log(`   Total Resources: ${this.metrics.totalResources}`);
    console.log(`   JavaScript Files: ${this.metrics.jsResources}`);
    console.log(`   CSS Files: ${this.metrics.cssResources}`);

    console.log('💾 Transfer Sizes:');
    console.log(`   Total Size: ${(this.metrics.totalSize / 1024).toFixed(2)}KB`);
    console.log(`   JavaScript: ${(this.metrics.jsSize / 1024).toFixed(2)}KB`);
    console.log(`   CSS: ${(this.metrics.cssSize / 1024).toFixed(2)}KB`);

    console.log('🎯 Optimization Status:');
    console.log(`   Predictive Search: ${this.isScriptLoaded('predictive-search.js') ? '❌ Enabled' : '✅ Disabled'}`);
    console.log(`   Animations: ${this.isScriptLoaded('animations.js') ? '❌ Enabled' : '✅ Disabled'}`);
    console.log(`   Cart Drawer: ${this.isScriptLoaded('cart-drawer.js') ? '❌ Enabled' : '✅ Disabled'}`);
    console.log(`   Component Loader: ❌ Disabled (commented out)`);

    console.groupEnd();
  }

  /**
   * Export metrics for external monitoring
   */
  exportMetrics() {
    return {
      ...this.metrics,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
    };
  }
}

// Initialize performance monitoring
const performanceMonitor = new PerformanceMonitor();
performanceMonitor.measurePageLoad();

// Export for external use
window.PerformanceMonitor = PerformanceMonitor;
window.performanceMonitor = performanceMonitor;
